/**
 * Правила приоритета блоков кабинета хозяйства и карточки контура
 * (agroai-6al.34, дополнение владельца 04.10; журнал решений №271).
 *
 * Экран объекта — не статическая вёрстка, а ситуация: порядок и наличие
 * блоков решают ДАННЫЕ сущности. Правила объявлены здесь одним списком —
 * каждое называет блок, условие по фактам, вес и причину словами. Экран
 * рисует блоки в порядке убывания веса; блок без сработавшего правила не
 * рисуется вовсе (и причина этого тоже сказана — `hidden`). Гид ИИ читает те
 * же причины (каталог экранов: блок «Сейчас» и «Требует внимания»), поэтому
 * может объяснить, почему блок наверху.
 *
 * Чего здесь нет: оценки хозяйства («хорошее/плохое») и красного за «без
 * отчёта». Вес — это порядок показа, а не балл. Неизвестный факт (`null`) не
 * равен нулю: «всё в порядке» печатается только когда ВСЕ сигналы измерены и
 * равны нулю.
 */

import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';
import { countOf, WORDS } from '../../lib/format';

/**
 * Причины показа блоков кабинета. Печатаются под блоком строкой «Почему здесь:
 * …» (хоким, менеджер); несколько причин идут подряд через пробел, поэтому
 * каждая — законченное предложение с точкой.
 */
const m = defineMessages({
  now: { id: 'farm.priority.now', defaultMessage: 'Сейчас — первым всегда: идущие агроциклы, текущая работа и её окно, контуры под агроциклом.', description: 'Причина показа блока «Сейчас» в кабинете хозяйства (строка «Почему здесь», хоким, менеджер)' },
  offseason: { id: 'farm.priority.offseason', defaultMessage: 'Межсезонье: ни один агроцикл хозяйства не идёт на дату — сверху итоги прошлых агроциклов и подготовка.', description: 'Причина показа блока итогов вместо «Сейчас» в кабинете хозяйства (строка «Почему здесь»)' },
  qRed: { id: 'farm.priority.qRed', defaultMessage: 'Красный карантин на {count, plural, one {# контуре} few {# контурах} many {# контурах} other {# контурах}} — критичное состояние объекта, блок поднят наверх.', description: 'Причина подъёма блока карантина в кабинете хозяйства; {count} — число контуров с красным статусом' },
  qReqOverdue: { id: 'farm.priority.qReqOverdue', defaultMessage: 'Предписаний после срока, не устранено: {count}.', description: 'Причина показа блока карантина в кабинете хозяйства; {count} — число предписаний после срока' },
  qYellow: { id: 'farm.priority.qYellow', defaultMessage: 'Под наблюдением (жёлтый) {count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}.', description: 'Причина показа блока карантина в кабинете хозяйства; {count} — число контуров с жёлтым статусом' },
  overdue: { id: 'farm.priority.overdue', defaultMessage: 'После срока без отчёта: {works} в идущих агроциклах.', description: 'Причина показа блока сроков в кабинете хозяйства; {works} — число с единицей: «5 работ на полях»' },
  dueSoon: { id: 'farm.priority.dueSoon', defaultMessage: 'Срок близко: {works} без отчёта, окно закрывается.', description: 'Причина показа блока сроков в кабинете хозяйства; {works} — число с единицей: «3 работы на полях»' },
  waterGap: { id: 'farm.priority.waterGap', defaultMessage: '{count, plural, one {# полив} few {# полива} many {# поливов} other {# полива}} без подачи (поле × полив) в идущих агроциклах.', description: 'Причина показа блока воды в кабинете хозяйства; {count} — число пар «поле × полив» без поданной воды' },
  waterShare: { id: 'farm.priority.waterShare', defaultMessage: 'Подано {pct}% от запрошенного — меньше 90%.', description: 'Причина показа блока воды в кабинете хозяйства; {pct} — доля поданной воды, число' },
  conflicts: { id: 'farm.priority.conflicts', defaultMessage: 'Открытых расхождений отчёта и проверки: {count}.', description: 'Причина показа блока расхождений в кабинете хозяйства; {count} — число расхождений' },
  allClear: { id: 'farm.priority.allClear', defaultMessage: 'Сигналов нет (все измерены и равны нулю) — компактная строка, сверху остаётся текущая работа.', description: 'Причина показа компактной строки «всё в порядке» в кабинете хозяйства' },
  harvestActive: { id: 'farm.priority.harvestActive', defaultMessage: 'Идёт уборка, у хозяйства есть план сдачи — сдача против плана поднята.', description: 'Причина подъёма блока сдачи урожая в кабинете хозяйства' },
  harvest: { id: 'farm.priority.harvest', defaultMessage: 'У хозяйства есть план сдачи.', description: 'Причина показа блока сдачи урожая в кабинете хозяйства' },
  indicators: { id: 'farm.priority.indicators', defaultMessage: 'Ключевые показатели — после текущего состояния и проблем.', description: 'Причина показа блока ключевых показателей в кабинете хозяйства' },
  land: { id: 'farm.priority.land', defaultMessage: 'Земля, поля и агроциклы — справка после текущего.', description: 'Причина показа блока земли в кабинете хозяйства' },
  map: { id: 'farm.priority.map', defaultMessage: 'Карта хозяйства со слоями и датой.', description: 'Причина показа блока карты в кабинете хозяйства' },
  assignments: { id: 'farm.priority.assignments', defaultMessage: 'Поручения по хозяйству — справка внизу.', description: 'Причина показа блока поручений в кабинете хозяйства' },
  history: { id: 'farm.priority.history', defaultMessage: 'Прошлые агроциклы — внизу.', description: 'Причина показа блока истории агроциклов в кабинете хозяйства' },
  historyOffseason: { id: 'farm.priority.historyOffseason', defaultMessage: 'Межсезонье — акцент на итогах: история агроциклов поднята.', description: 'Причина подъёма блока истории в межсезонье, кабинет хозяйства' },
  historyPast: { id: 'farm.priority.historyPast', defaultMessage: 'Исторический срез на {date}: итоги агроциклов на эту дату подняты.', description: 'Причина подъёма блока истории при просмотре прошлой даты; {date} — дата среза, ISO' },
  hiddenHarvest: { id: 'farm.priority.hiddenHarvest', defaultMessage: 'У хозяйства нет плана сдачи в срезе — блок сдачи не показывается (пустые хлопковые блоки не рисуются).', description: 'Объяснение для ИИ-гида, почему блок сдачи скрыт в кабинете хозяйства' },
  hiddenNow: { id: 'farm.priority.hiddenNow', defaultMessage: 'Ни один агроцикл не идёт — вместо «Сейчас» итоги и подготовка.', description: 'Объяснение для ИИ-гида, почему блок «Сейчас» скрыт в кабинете хозяйства' },
  cNow: { id: 'farm.priority.contour.now', defaultMessage: 'Сейчас — первым: текущая культура и агроцикл, текущая работа и её статус.', description: 'Причина показа блока «Сейчас» в карточке контура (строка «Почему здесь»)' },
  cQRed: { id: 'farm.priority.contour.qRed', defaultMessage: 'Контур в красном карантине — критичное состояние, блок наверху.', description: 'Причина подъёма блока карантина в карточке контура' },
  cQReqOverdue: { id: 'farm.priority.contour.qReqOverdue', defaultMessage: 'Предписаний после срока: {count}.', description: 'Причина показа блока карантина в карточке контура; {count} — число предписаний после срока' },
  cQYellow: { id: 'farm.priority.contour.qYellow', defaultMessage: 'Контур под наблюдением (жёлтый).', description: 'Причина показа блока карантина в карточке контура' },
  cQReqOpen: { id: 'farm.priority.contour.qReqOpen', defaultMessage: 'Открытых предписаний: {count}.', description: 'Причина показа блока карантина в карточке контура; {count} — число открытых предписаний' },
  cOverdue: { id: 'farm.priority.contour.overdue', defaultMessage: 'Работ после срока без отчёта: {count}.', description: 'Причина показа блока работ в карточке контура; {count} — число работ после срока' },
  cWaterShort: { id: 'farm.priority.contour.waterShort', defaultMessage: 'Позиций воды с недоподачей: {count}.', description: 'Причина показа блока воды в карточке контура; {count} — число позиций, где подано меньше запрошенного' },
  cAllClear: { id: 'farm.priority.contour.allClear', defaultMessage: 'Активных проблем нет — компактная строка.', description: 'Причина показа строки «всё в порядке» в карточке контура' },
  cPassport: { id: 'farm.priority.contour.passport', defaultMessage: 'Паспорт — справка после текущего.', description: 'Причина показа паспорта в карточке контура' },
  cMap: { id: 'farm.priority.contour.map', defaultMessage: 'Контур и соседи на карте.', description: 'Причина показа карты в карточке контура' },
});

/* ------------------------------------------------------------ хозяйство */

export type FarmBlock =
  | 'now'
  | 'season-results'
  | 'alert-quarantine'
  | 'alert-deadlines'
  | 'alert-water'
  | 'alert-conflicts'
  | 'all-clear'
  | 'indicators'
  | 'harvest'
  | 'land'
  | 'map'
  | 'assignments'
  | 'history';

/** Факты хозяйства на дату среза. `null` — не измерено (не ноль). */
export interface FarmFacts {
  /** Дата среза, ISO. */
  asOf: string;
  /** Сегодня, ISO. Срез раньше сегодняшнего — исторический. */
  today: string;
  /** Циклов хозяйства в статусе «идёт» на дату. */
  activeCycles: number;
  /** Культуры (коды) всех циклов хозяйства в срезе — любого статуса. */
  crops: string[];
  /** Обязательства после срока в идущих циклах. */
  overdue: number | null;
  /** Обязательства, срок которых близко (окно закрывается в ближайшие дни). */
  dueSoon: number | null;
  /** Контуры с недоподачей воды в поливах идущих циклов. */
  waterGapContours: number | null;
  /** Подано от запрошенного, %. */
  waterSharePct: number | null;
  /** Открытые расхождения отчёта и проверки. */
  conflicts: number | null;
  redContours: number | null;
  yellowContours: number | null;
  /** Предписания карантина после срока, не устранены. */
  requirementsOverdue: number | null;
  /** План сдачи хозяйства, т (нет плана — 0 или null). */
  harvestPlanT: number | null;
  /** Идёт ли уборка в каком-то из идущих циклов (стадия цикла). */
  harvestActive: boolean;
}

export interface FarmRule {
  id: string;
  block: FarmBlock;
  when: (f: FarmFacts) => boolean;
  weight: number;
  reason: (f: FarmFacts) => string;
}

const n = (v: number | null): number => v ?? 0;
const known = (...values: (number | null)[]) => values.every((v) => v !== null);
export const isHistorical = (f: { asOf: string; today: string }) => f.asOf < f.today;
export const hasCrop = (f: FarmFacts, code: string) => f.crops.includes(code);

/** Сигналов нет, и это ИЗМЕРЕНО (ни одного `null`). */
export function farmAllClear(f: FarmFacts): boolean {
  const signals = [f.overdue, f.dueSoon, f.redContours, f.yellowContours, f.requirementsOverdue, f.waterGapContours, f.conflicts];
  return known(...signals) && signals.every((v) => v === 0);
}

/**
 * ЕДИНСТВЕННЫЙ список правил хозяйства. Порядок строк значения не имеет —
 * решает вес; у блока берётся наибольший вес сработавших правил, причины
 * собираются все.
 */
export const FARM_RULES: FarmRule[] = [
  { id: 'now', block: 'now', weight: 1000, when: (f) => f.activeCycles > 0, reason: () => tr(m.now) },
  { id: 'offseason', block: 'season-results', weight: 1000, when: (f) => f.activeCycles === 0, reason: () => tr(m.offseason) },

  { id: 'q-red', block: 'alert-quarantine', weight: 960, when: (f) => n(f.redContours) > 0, reason: (f) => tr(m.qRed, { count: Number(f.redContours) }) },
  { id: 'q-req-overdue', block: 'alert-quarantine', weight: 920, when: (f) => n(f.requirementsOverdue) > 0, reason: (f) => tr(m.qReqOverdue, { count: Number(f.requirementsOverdue) }) },
  { id: 'q-yellow', block: 'alert-quarantine', weight: 640, when: (f) => n(f.yellowContours) > 0, reason: (f) => tr(m.qYellow, { count: Number(f.yellowContours) }) },

  { id: 'overdue', block: 'alert-deadlines', weight: 900, when: (f) => n(f.overdue) > 0, reason: (f) => tr(m.overdue, { works: countOf(Number(f.overdue), WORDS.obligation) }) },
  { id: 'due-soon', block: 'alert-deadlines', weight: 600, when: (f) => n(f.dueSoon) > 0, reason: (f) => tr(m.dueSoon, { works: countOf(Number(f.dueSoon), WORDS.obligation) }) },

  { id: 'water-gap', block: 'alert-water', weight: 760, when: (f) => n(f.waterGapContours) > 0, reason: (f) => tr(m.waterGap, { count: Number(f.waterGapContours) }) },
  { id: 'water-share', block: 'alert-water', weight: 620, when: (f) => f.waterSharePct !== null && f.waterSharePct < 90, reason: (f) => tr(m.waterShare, { pct: Number(f.waterSharePct) }) },

  { id: 'conflicts', block: 'alert-conflicts', weight: 560, when: (f) => n(f.conflicts) > 0, reason: (f) => tr(m.conflicts, { count: Number(f.conflicts) }) },

  { id: 'all-clear', block: 'all-clear', weight: 950, when: farmAllClear, reason: () => tr(m.allClear) },

  { id: 'harvest-active', block: 'harvest', weight: 780, when: (f) => n(f.harvestPlanT) > 0 && f.harvestActive, reason: () => tr(m.harvestActive) },
  { id: 'harvest', block: 'harvest', weight: 350, when: (f) => n(f.harvestPlanT) > 0, reason: () => tr(m.harvest) },

  { id: 'indicators', block: 'indicators', weight: 500, when: () => true, reason: () => tr(m.indicators) },
  { id: 'land', block: 'land', weight: 450, when: () => true, reason: () => tr(m.land) },
  { id: 'map', block: 'map', weight: 250, when: () => true, reason: () => tr(m.map) },
  { id: 'assignments', block: 'assignments', weight: 150, when: () => true, reason: () => tr(m.assignments) },
  { id: 'history', block: 'history', weight: 100, when: () => true, reason: () => tr(m.history) },
  { id: 'history-offseason', block: 'history', weight: 980, when: (f) => f.activeCycles === 0, reason: () => tr(m.historyOffseason) },
  { id: 'history-past', block: 'history', weight: 700, when: (f) => isHistorical(f), reason: (f) => tr(m.historyPast, { date: f.asOf }) },
];

/** Почему блок НЕ показан — тоже правило, а не молчание. */
export const FARM_HIDDEN: { block: FarmBlock; when: (f: FarmFacts) => boolean; reason: () => string }[] = [
  { block: 'harvest', when: (f) => n(f.harvestPlanT) === 0, reason: () => tr(m.hiddenHarvest) },
  { block: 'now', when: (f) => f.activeCycles === 0, reason: () => tr(m.hiddenNow) },
];

export interface Arranged<B extends string> {
  block: B;
  weight: number;
  reasons: string[];
  rules: string[];
}

function arrange<B extends string, F>(
  rules: { id: string; block: B; when: (f: F) => boolean; weight: number; reason: (f: F) => string }[],
  facts: F,
): Arranged<B>[] {
  const out = new Map<B, Arranged<B>>();
  for (const rule of rules) {
    if (!rule.when(facts)) continue;
    const hit = out.get(rule.block) ?? { block: rule.block, weight: -Infinity, reasons: [], rules: [] };
    hit.weight = Math.max(hit.weight, rule.weight);
    hit.reasons.push(rule.reason(facts));
    hit.rules.push(rule.id);
    out.set(rule.block, hit);
  }
  return [...out.values()].sort((a, b) => b.weight - a.weight);
}

export function arrangeFarm(facts: FarmFacts): { shown: Arranged<FarmBlock>[]; hidden: { block: FarmBlock; reason: string }[] } {
  const shown = arrange(FARM_RULES, facts);
  const shownIds = new Set(shown.map((s) => s.block));
  const hidden = FARM_HIDDEN.filter((h) => !shownIds.has(h.block) && h.when(facts)).map(({ block, reason }) => ({ block, reason: reason() }));
  return { shown, hidden };
}

/* ------------------------------------------------------------- контур */

export type ContourBlock =
  | 'now'
  | 'alert-quarantine'
  | 'alert-works'
  | 'alert-water'
  | 'all-clear'
  | 'passport'
  | 'map';

export interface ContourFacts {
  asOf: string;
  today: string;
  quarantineStatus: 'green' | 'yellow' | 'red' | null;
  requirementsOpen: number | null;
  requirementsOverdue: number | null;
  overdueWorks: number | null;
  inWindowWorks: number | null;
  /** Позиции воды контура с подачей меньше запрошенного. */
  waterShort: number | null;
}

export interface ContourRule {
  id: string;
  block: ContourBlock;
  when: (f: ContourFacts) => boolean;
  weight: number;
  reason: (f: ContourFacts) => string;
}

export function contourAllClear(f: ContourFacts): boolean {
  return (
    f.quarantineStatus === 'green' &&
    known(f.requirementsOpen, f.overdueWorks, f.waterShort) &&
    f.requirementsOpen === 0 &&
    f.overdueWorks === 0 &&
    f.waterShort === 0
  );
}

export const CONTOUR_RULES: ContourRule[] = [
  { id: 'now', block: 'now', weight: 1000, when: () => true, reason: () => tr(m.cNow) },
  { id: 'q-red', block: 'alert-quarantine', weight: 960, when: (f) => f.quarantineStatus === 'red', reason: () => tr(m.cQRed) },
  { id: 'q-req-overdue', block: 'alert-quarantine', weight: 930, when: (f) => n(f.requirementsOverdue) > 0, reason: (f) => tr(m.cQReqOverdue, { count: Number(f.requirementsOverdue) }) },
  { id: 'q-yellow', block: 'alert-quarantine', weight: 700, when: (f) => f.quarantineStatus === 'yellow', reason: () => tr(m.cQYellow) },
  { id: 'q-req-open', block: 'alert-quarantine', weight: 650, when: (f) => n(f.requirementsOpen) > 0, reason: (f) => tr(m.cQReqOpen, { count: Number(f.requirementsOpen) }) },
  { id: 'overdue', block: 'alert-works', weight: 880, when: (f) => n(f.overdueWorks) > 0, reason: (f) => tr(m.cOverdue, { count: Number(f.overdueWorks) }) },
  { id: 'water-short', block: 'alert-water', weight: 600, when: (f) => n(f.waterShort) > 0, reason: (f) => tr(m.cWaterShort, { count: Number(f.waterShort) }) },
  { id: 'all-clear', block: 'all-clear', weight: 950, when: contourAllClear, reason: () => tr(m.cAllClear) },
  { id: 'passport', block: 'passport', weight: 400, when: () => true, reason: () => tr(m.cPassport) },
  { id: 'map', block: 'map', weight: 300, when: () => true, reason: () => tr(m.cMap) },
];

export function arrangeContour(facts: ContourFacts): Arranged<ContourBlock>[] {
  return arrange(CONTOUR_RULES, facts);
}
